import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { coreAPI } from '../lib/api';
import { useView } from '../contexts/ViewContext';
import { toast } from 'sonner';

/** Portal a deep link may ask for. */
type LinkPortal = 'customer' | 'supplier';

/** Session key holding the link a portal-switch reload was made for, and when. */
const RELOADED_KEY = 'deepLinkSite:reloaded';
/** A marker older than this is stale (the reload never landed) and is ignored. */
const RELOAD_MARKER_MS = 30_000;

/** Whether this page load is the reload a portal switch made for this link. */
const isOwnReload = (raw: string | null): boolean => {
  if (!raw) return false;
  try {
    const marker = JSON.parse(raw) as { href?: string; at?: number };
    return marker.href === window.location.href
      && typeof marker.at === 'number' && Date.now() - marker.at < RELOAD_MARKER_MS;
  } catch {
    return false;
  }
};

const isLinkPortal = (value: string | null): value is LinkPortal =>
  value === 'customer' || value === 'supplier';

/**
 * Switch the view to the site a deep link names, before the page loads its data.
 *
 * CR-6457: each count in the Weekly Arrears Report email links to PO Lines for
 * one site (`?site=<company id>&portal=customer|supplier`). Lists are scoped by
 * the top-bar view, so a link for Daresbury opened while viewing Toulouse would
 * show Toulouse's lines — a different number from the email. This hook moves
 * the view to the linked site first, once, and only to a site the user already
 * holds; anything else is ignored with a toast. A portal switch reloads the
 * app, as the top-bar toggle and the supplier chat panel do, after saving the
 * site so the reload lands back on the same link. The `site` and `portal`
 * params are then dropped from the URL, so the page's own filters still read
 * the rest and a later switcher change is never undone.
 *
 * @returns `true` once the page may render (no site in the link, or the view
 *   now shows that site); `false` while the switch is in flight.
 *
 * @example
 * const ready = useDeepLinkSite();
 * if (!ready) return <Loader2 className="animate-spin" />;
 */
export function useDeepLinkSite(): boolean {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    isLoading, isSaving, accessibleSites, accessibleCompanies, viewLevel,
    selectedSiteId, selectedCompanyId, portalMode, supportsDualMode,
    selectSite, selectCompany,
  } = useView();
  const rawSite = searchParams.get('site');
  const siteId = rawSite && /^\d+$/.test(rawSite) ? Number(rawSite) : null;
  const rawPortal = searchParams.get('portal');
  const portal = isLinkPortal(rawPortal) ? rawPortal : null;
  const [ready, setReady] = useState(siteId === null);
  const startedRef = useRef(false);

  const viewingTarget = siteId !== null && (
    (viewLevel === 'SITE' && selectedSiteId === siteId)
    || (viewLevel === 'COMPANY' && selectedCompanyId === siteId)
  );
  const portalMatches = !portal || !supportsDualMode || portalMode === portal;

  useEffect(() => {
    if (ready || siteId === null || isLoading) return;

    const finish = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('site');
      next.delete('portal');
      setSearchParams(next, { replace: true });
      setReady(true);
    };

    if (startedRef.current) {
      // Waiting for the switch below to land in the view (and the database).
      if (viewingTarget && portalMatches && !isSaving) finish();
      return;
    }
    startedRef.current = true;

    // One reload per link at most: if the view still disagrees after it, the
    // page is shown as it is rather than reloading again. Read and cleared on
    // every arrival, so a later click on the same link starts afresh.
    let alreadyReloaded = false;
    try {
      alreadyReloaded = isOwnReload(sessionStorage.getItem(RELOADED_KEY));
      sessionStorage.removeItem(RELOADED_KEY);
    } catch {
      // Storage unavailable: fall through, the save below still lands the view.
    }

    if (viewingTarget && portalMatches) {
      finish();
      return;
    }
    const site = accessibleSites.find((s) => s.id === siteId);
    const company = site ? undefined : accessibleCompanies.find((c) => c.id === siteId);
    if (!site && !company) {
      toast.info('That link is for a site you do not have access to, so your current view is shown.');
      finish();
      return;
    }
    const name = site?.name ?? company?.name ?? '';
    if (!portalMatches && portal && !alreadyReloaded) {
      coreAPI.updateViewContext({
        current_view_level: site ? 'SITE' : 'COMPANY', current_group: null,
        current_company: site ? null : siteId, current_site: siteId, portal_mode: portal,
      })
        .then(() => {
          try {
            sessionStorage.setItem(RELOADED_KEY, JSON.stringify({ href: window.location.href, at: Date.now() }));
          } catch { /* best effort */ }
          window.location.reload();
        })
        .catch((err: unknown) => {
          console.error('Failed to switch portal for a deep link:', err);
          toast.error('Could not switch portal; your current view is shown.');
          finish();
        });
      return;
    }
    if (!portalMatches) {
      finish();
      return;
    }
    if (site) selectSite(site.id, site.name);
    else if (company) selectCompany(company.id, company.name);
    toast.info(`Now viewing ${name}.`);
  }, [ready, siteId, isLoading, isSaving, viewingTarget, portalMatches, portal,
    accessibleSites, accessibleCompanies, selectSite, selectCompany,
    searchParams, setSearchParams]);

  return ready;
}
